import type {
  LanguageServiceProvider,
  LanguageServiceCompletionResult,
  IntelliSenseStatus,
  IntelliSenseStatusListener,
  LanguageServicePosition,
  LanguageServiceConnectionInfo,
} from './languageServiceProvider';

import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import { ensureMysqlSchema, getCachedMysqlSchema } from './mysqlSchemaCache';
import { getMysqlHoverInfo } from './mysqlHover';
import { resolveSchemaReadyStatus, subscribeSchemaReadyStatus } from './introspectionTruncation';

type DocumentState = {
  text: string;
  connectionId?: string;
  database?: string;
  status: IntelliSenseStatus;
  statusMessage?: string;
};

const documents = new Map<string, DocumentState>();
const statusListeners: IntelliSenseStatusListener[] = [];

function emitStatus(uri: string, status: IntelliSenseStatus, message?: string): void {
  const doc = documents.get(uri);
  // S7 item 5 — a `ready` flip becomes `partial` (completion is live, minus
  // the artefacts an introspection row cap clipped) whenever a clip stands
  // for this document's (connectionId, database). Filtered here, at the one
  // emit choke point, so every readiness path stays honest.
  let nextStatus = status;
  let nextMessage = message;
  if (status === 'ready') {
    const resolved = resolveSchemaReadyStatus(doc?.connectionId, doc?.database);
    nextStatus = resolved.status;
    nextMessage = resolved.message ?? message;
  }

  if (doc) {
    doc.status = nextStatus;
    doc.statusMessage = nextMessage;
  }

  for (const listener of statusListeners) {
    listener({ uri, status: nextStatus, message: nextMessage });
  }
}

function getDocument(uri: string): DocumentState {
  return documents.get(uri) ?? { text: '', status: 'unavailable' };
}

// A clip or failure recorded AFTER a document went `ready` (phase 2 lands
// later than the readiness emit) must still reach the pill, and a clean
// retry must take it back — re-derive every affected document's status
// whenever the registry changes (release review 1.5.0, R2 / R3).
subscribeSchemaReadyStatus((connectionId, database) => {
  for (const [uri, doc] of documents) {
    if (doc.connectionId !== connectionId) continue;
    if ((doc.database ?? '').toLowerCase() !== database.toLowerCase()) continue;
    if (doc.status !== 'ready' && doc.status !== 'partial') continue;
    emitStatus(uri, 'ready');
  }
});

export const mysqlLanguageServiceProvider: LanguageServiceProvider = {
  engine: 'mysql',
  ensureInitialized: async () => {},

  onIntelliSenseStatus: (listener) => {
    statusListeners.push(listener);
    return () => {
      const idx = statusListeners.indexOf(listener);
      if (idx >= 0) statusListeners.splice(idx, 1);
    };
  },

  getIntelliSenseStatus: (uri) => documents.get(uri)?.status ?? 'unavailable',

  registerDocument: async (uri: string, text: string) => {
    documents.set(uri, { text, status: 'unavailable' });
    return true;
  },

  updateDocument: async (uri: string, text: string) => {
    const existing = documents.get(uri);
    if (existing) {
      existing.text = text;
    } else {
      documents.set(uri, { text, status: 'unavailable' });
    }
    return true;
  },

  closeDocument: async (uri: string) => {
    documents.delete(uri);
    return true;
  },

  connectForLanguageServices: async (
    uri: string,
    connectionInfo: LanguageServiceConnectionInfo,
  ): Promise<boolean> => {
    const doc = documents.get(uri) ?? { text: '', status: 'unavailable' };
    doc.connectionId = connectionInfo.connectionId;
    doc.database = connectionInfo.database?.trim() || '';
    documents.set(uri, doc);

    emitStatus(uri, 'initializing', 'Loading MySQL schema…');

    try {
      // S7 item 3 — the snapshot is built once per (connection, database) and
      // rebuilt only by DDL invalidation, an explicit refresh, or reconnect.
      // Connect therefore forces ONLY when there is nothing to serve: no
      // native snapshot at all, or a bus that was cleared (disconnect /
      // reconnect, `invalidateConnection`) while the native cache stayed warm
      // — the cache's warm path returns without re-publishing, so without this
      // the reconnected editor would sit on an empty bus. A warm snapshot plus
      // a warm bus re-uses both and issues no introspection at all.
      const existingSchema = getCachedMysqlSchema(doc.connectionId);
      const hasBusSnapshot = Boolean(getSchemaBusSnapshot(doc.connectionId, doc.database));
      const shouldForceRefresh = !existingSchema || !hasBusSnapshot;

      await ensureMysqlSchema({
        connectionId: doc.connectionId,
        database: doc.database,
        forceRefresh: shouldForceRefresh,
      });
      emitStatus(uri, 'ready');
      return true;
    } catch (error) {
      emitStatus(uri, 'error', error instanceof Error ? error.message : 'Failed to load MySQL schema');
      return false;
    }
  },

  disconnectForLanguageServices: async (uri: string) => {
    const doc = documents.get(uri);
    if (doc) {
      doc.connectionId = undefined;
      doc.database = undefined;
      emitStatus(uri, 'unavailable');
    }
    return true;
  },

  // MySQL completion is fully orchestrator-driven (`mysqlResolver` + schema
  // cache). Only the mock engine's LSP bridge ever consults a provider for
  // completion items, so this arm is intentionally empty — it must never
  // become a second completion source. (Workstream A — legacy per-engine
  // completion layer retirement; the old dt-sql-parser-backed arm lived
  // here.)
  getCompletionItems: async (): Promise<LanguageServiceCompletionResult> => ({ items: [] }),

  getSignatureHelp: async () => null,

  getHoverInfo: async (uri: string, position: LanguageServicePosition) => {
    const doc = getDocument(uri);
    const connectionId = doc.connectionId;
    const database = doc.database ?? '';
    let snapshot = connectionId ? getCachedMysqlSchema(connectionId) : undefined;

    // Hover warms a cold cache only. The mid-session TTL re-ensure that used
    // to live here re-ran the whole introspection fan-out roughly every minute
    // of editing (S7 item 3); a warm snapshot is now served as-is and rebuilt
    // only by DDL invalidation, an explicit refresh, or reconnect.
    if (connectionId && !snapshot) {
      try {
        snapshot = await ensureMysqlSchema({ connectionId, database });
      } catch {
        snapshot = undefined;
      }
    }
    return getMysqlHoverInfo({ text: doc.text, position, snapshot });
  },
};
